/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/KanbanBoard" />
import * as React from "react";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { css, IVssLayoutManager } from "VSS/Platform/Layout";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Observer } from "VSSUI/Observer";
import { KeyCode } from "VSSUI/Util";
import { IBoardsWorkItemService } from "Wit/BoardsHub/Common/IBoardsWorkItemService";
import { IObservableBoardsWorkItem } from "Wit/BoardsHub/Common/Interfaces";
import { boardPieceExpandedKey } from "Wit/BoardsHub/Common/KanbanBoardConstants";
import { BoardColumnLayout, BoardLayout, IBoardSettingsData } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import { BoardColumnsHeader, MainBoardColumn } from "Wit/BoardsHub/KanbanBoard/Components/BoardColumn";
import { BoardRow } from "Wit/BoardsHub/KanbanBoard/Components/BoardRow";
import { IKanbanBoardService } from "Wit/BoardsHub/KanbanBoard/IKanbanBoardService";
import { BoardEditPermissionProvider } from "Wit/BoardsHub/KanbanBoard/Utils/BoardEditPermissionContext";
import { AgileBacklogPermissionContext } from "Wit/Common/Controls/Boards/AgileBacklogPermissionContext";
import { DragMode, findElementAtPoint, IPosition, ScrollableData } from "Wit/Common/Controls/Boards/Draggable";
import { findElementClosestToVerticalPoint, IInsertionMarkerPosition, InsertionMarker } from "Wit/Common/Controls/Boards/InsertionMarker";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { WorkItemFormDialog } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { registerKeyboardShortcuts, unregisterKeyboardShortcuts } from "../BoardKeyboardShortcuts";
import { ColumnNavigationGraph } from "../ColumnNavigationGraph";
import { focusCard, isIncoming, isInProgress, isOutgoing } from "../Utils/BoardUtils";
import { openDialog } from "./Util";

interface IKanbanBoardProps {
    boardLayout: IReadonlyObservableValue<BoardLayout | undefined>;
    boardSettings: IReadonlyObservableValue<IBoardSettingsData | undefined>;
    onMarkTTI: () => void;
    onToggleFilter: (toggled: boolean) => void;
    scrollableData?: ScrollableData;
}

enum Direction {
    Up,
    Down,
    Left,
    Right
}

export class KanbanBoard extends TfsComponent<IKanbanBoardProps, {}> {
    private _hasBacklogManagementPermission = false;
    private _highlightedElement: HTMLElement | undefined;
    private _insertionMarker = new ObservableValue<IInsertionMarkerPosition | undefined>(undefined);
    private _navigationGraph: ColumnNavigationGraph;
    private _previousColumName: string;
    private _previousRowName: string | undefined | null;
    private _previousSplitColumn: boolean | undefined;
    private _columnChanged = new ObservableValue<boolean>(false);
    private _doneChanged = new ObservableValue<boolean>(false);
    private _rowChanged = new ObservableValue<boolean>(false);
    private isLoading = true;

    public componentDidMount() {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");

        this.updateNavigationGraph();
        registerKeyboardShortcuts(
            this.context.pageContext,
            () => this.props.onToggleFilter(true),
            () => layoutManager.toggleFullScreenMode()
        );
    }

    public componentWillUnmount() {
        unregisterKeyboardShortcuts(this.context.pageContext);
    }

    public render(): JSX.Element | null {
        const { boardLayout, boardSettings, scrollableData } = this.props;
        return (
            <AgileBacklogPermissionContext.Consumer>
                {({ hasBacklogManagementPermission }) => {
                    this._hasBacklogManagementPermission = hasBacklogManagementPermission;
                    return (
                        <Observer
                            boardLayout={{
                                observableValue: boardLayout,
                                filter: (bl: BoardLayout | undefined) => {
                                    this.updateNavigationGraph();
                                    return bl;
                                }
                            }}
                            boardSettings={boardSettings}
                        >
                            {() => {
                                if (!boardLayout.value || !boardSettings.value) {
                                    return null;
                                }

                                if (this.isLoading) {
                                    this.isLoading = false;
                                    this.props.onMarkTTI();
                                }

                                const { columns, incomingColumn, outgoingColumn, rows, ...columnProps } = boardLayout.value;
                                const boardId = boardSettings.value.boardData.boardModel.board.id;

                                const onSaveToggle = (expanded: boolean, id: string) => {
                                    const storageKey = boardPieceExpandedKey(id);
                                    const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
                                    const entries: { [key: string]: any } = {
                                        [storageKey]: expanded
                                    };
                                    settingsService.setEntries(entries, SettingsUserScope.Me, "team", this.teamData?.id);
                                };

                                // the default row can't be deleted and isn't visible if it is the only row
                                const showRows = rows.length > 1;
                                let rowContent = (
                                    <>
                                        {rows.map((row, index) => {
                                            // let the last row grow to fill the remaining space at the bottom of the page
                                            return (
                                                <BoardRow
                                                    className={css(index === rows.length - 1 && "flex-grow")}
                                                    key={row.id}
                                                    rowLayout={row}
                                                    showRow={showRows}
                                                    onCardDrag={this.onCardDrag}
                                                    onCardKeyDown={this.onCardKeyDown}
                                                    onCardFocus={this.onFocus}
                                                    onSaveToggle={onSaveToggle}
                                                    columnChanged={this._columnChanged}
                                                    rowChanged={this._rowChanged}
                                                    scrollableData={scrollableData}
                                                    doneChanged={this._doneChanged}
                                                    {...columnProps}
                                                />
                                            );
                                        })}
                                    </>
                                );

                                // double count split columns, which take up twice the space of a normal column
                                const columnCount = columns.length + columns.filter(c => c.isSplit).length;
                                const inProgressColumnCount = columnCount - 2;
                                const rowContentMaxWidth = inProgressColumnCount * totalMaxColumnWidth;
                                const rowContentMinWidth = inProgressColumnCount * totalMinColumnWidth;
                                if (showRows) {
                                    rowContent = (
                                        <div
                                            className="flex-column kanban-board-row-container rhythm-vertical-4"
                                            style={{ flexBasis: rowContentMaxWidth, minWidth: rowContentMinWidth }}
                                        >
                                            {rowContent}
                                        </div>
                                    );
                                }

                                // calculate the total minWidth of the board so white background extends across page
                                // when horizontal scroll is required. 32 is the total horizontal padding
                                const boardMinWidth = rowContentMinWidth + totalMinColumnWidth * 2 + 32;
                                return (
                                    <BoardEditPermissionProvider
                                        hasBoardEditPermission={boardSettings.value.boardData.boardModel.boardSettings.canEdit}
                                    >
                                        <div
                                            className="flex-column flex-grow kanban-board padding-horizontal-16 body-s"
                                            style={{ minWidth: boardMinWidth }}
                                        >
                                            <InsertionMarker insertionMarkerPosition={this._insertionMarker} />
                                            <BoardColumnsHeader columns={columns} onSaveToggle={onSaveToggle} />
                                            <div className="flex-row flex-grow kanban-board-content">
                                                <MainBoardColumn
                                                    boardId={boardId}
                                                    columnLayout={incomingColumn}
                                                    onAddItem={this.onAddItem}
                                                    onCardDrag={this.onCardDrag}
                                                    onCardKeyDown={this.onCardKeyDown}
                                                    onCardFocus={this.onFocus}
                                                    onPageAdditionalItems={this.onPageAdditionalItems}
                                                    wrapColumn={showRows}
                                                    columnChanged={this._columnChanged}
                                                    rowChanged={this._rowChanged}
                                                    scrollableData={scrollableData}
                                                    doneChanged={this._doneChanged}
                                                    {...columnProps}
                                                />
                                                {rowContent}
                                                <MainBoardColumn
                                                    columnLayout={outgoingColumn}
                                                    onCardDrag={this.onCardDrag}
                                                    onCardKeyDown={this.onCardKeyDown}
                                                    onCardFocus={this.onFocus}
                                                    onPageAdditionalItems={this.onPageAdditionalItems}
                                                    wrapColumn={showRows}
                                                    columnChanged={this._columnChanged}
                                                    rowChanged={this._rowChanged}
                                                    scrollableData={scrollableData}
                                                    doneChanged={this._doneChanged}
                                                    {...columnProps}
                                                />
                                            </div>
                                        </div>
                                    </BoardEditPermissionProvider>
                                );
                            }}
                        </Observer>
                    );
                }}
            </AgileBacklogPermissionContext.Consumer>
        );
    }

    private onAddItem = async (title: string, workItemType: string): Promise<void> => {
        const { boardLayout, boardSettings } = this.props;
        if (!workItemType || !boardSettings.value) {
            return;
        }

        const workItemService = this.context.pageContext.getService<IBoardsWorkItemService>("IBoardsWorkItemService");
        const defaultColumn = boardLayout.value?.columns.find(column => isIncoming(column));
        const defaultRow = null;
        if (!defaultColumn) {
            return;
        }

        const extensionId = boardSettings.value.boardData.boardModel.boardSettings.extensionId;
        const { columnField, rowField } = boardSettings.value.boardFields;
        const fieldDictionary: INamedFieldDataDictionary = {
            [columnField]: defaultColumn.name,
            [rowField]: defaultRow
        };
        const nextItemId = defaultColumn.workItems.value[0]?.value.id;
        if (!title) {
            return openDialog(this.context.pageContext, onDismiss => {
                return (
                    <WorkItemFormDialog
                        onDismiss={onDismiss}
                        workItemTypeName={workItemType}
                        onSaveComplete={workItem => {
                            workItemService.createNewWorkItem(extensionId, workItemType, fieldDictionary, undefined, nextItemId, workItem);
                        }}
                    />
                );
            });
        }

        fieldDictionary[CoreFieldRefNames.Title] = title;
        await workItemService.createNewWorkItem(extensionId, workItemType, fieldDictionary, undefined, nextItemId);
    };

    private onCardDrag = (e: PointerEvent, card: HTMLElement, mode: DragMode, position: IPosition): undefined => {
        const kanbanService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
        const column = findElementAtPoint(".kanban-board-column", e.pageX, e.pageY);
        const doneColumn = column?.dataset.done === "true";
        const row = findElementAtPoint(".kanban-board-row", e.pageX, e.pageY);
        const rowId = row?.dataset.row;
        const itemId = Number(card.dataset.itemid);
        const columnRect = column?.getBoundingClientRect();
        let closestCard: HTMLElement | undefined;
        if (columnRect) {
            closestCard = column && findElementClosestToVerticalPoint(column, ".wit-card", e.pageY);
        }
        const closestItemId = Number(closestCard?.dataset.itemid);
        const closestRect = closestCard?.getBoundingClientRect();

        const columnId = column?.dataset.column ?? this.getColumnIdFromRowIdAndItemId(rowId, itemId, kanbanService);
        const highlightSwimlane = !column?.dataset.column;

        const currentItemColumn = kanbanService.getWorkItemColumnLayout(itemId);
        const columnLayout = kanbanService.getColumnLayout(columnId, rowId, doneColumn);

        const currentRow = kanbanService.getWorkItemRowLayout(itemId);

        // if we're in the bottom half of the row, insert below the row
        const below = closestRect && e.pageY > closestRect.top + window.scrollY + closestRect.height / 2;

        const isDoneChangedOnly = currentItemColumn?.id === columnLayout?.id 
                                && currentItemColumn?.isSplit 
                                && currentItemColumn.isDone !== columnLayout?.isDone
                                && currentRow?.id === rowId;

        const canReorder = this._hasBacklogManagementPermission && kanbanService.canReorderInColumn(columnId, itemId, doneColumn);
        const [previousId, nextId] = canReorder ? this.getColumnPlacementIndexes(columnLayout, closestItemId, below) : [undefined, undefined];
        if (e.type === "pointermove") {
            const highlightingElement = highlightSwimlane ? row : column;
            if (highlightingElement !== this._highlightedElement) {
                highlightingElement?.classList.add("highlighted");
                this._highlightedElement?.classList.remove("highlighted");
                this._highlightedElement = highlightingElement;
            }

            if (canReorder) {
                this.updateReorderMarker(itemId, column, columnRect, closestCard, closestItemId, below, previousId, nextId, isDoneChangedOnly);
            } else if (this._insertionMarker.value) {
                this.clearReorderMarker();
            }
        } else if (e.type === "pointerup") {
            this._highlightedElement?.classList.remove("highlighted");
            this._highlightedElement = undefined;
            this.clearReorderMarker();
            if (columnId && itemId && ((previousId !== itemId && nextId !== itemId) || isDoneChangedOnly)) {
                kanbanService.moveItem(itemId, columnId, rowId, doneColumn, previousId, nextId);
            }
        }

        return;
    };

    private onFocus = (columnLayout: BoardColumnLayout, rowName?: string) => {
        if (columnLayout.name != null && (!this._previousColumName || this._previousColumName !== columnLayout.name)) {
            this._previousColumName = columnLayout.name;

            this._columnChanged.value = true;

            this._previousSplitColumn = columnLayout.isDone;
            this._doneChanged.value = true;
        }

        if (columnLayout.name != null && columnLayout.isSplit && this._previousSplitColumn !== columnLayout.isDone) {
            this._previousSplitColumn = columnLayout.isDone;
            this._doneChanged.value = true;
        }

        if (this._previousRowName !== rowName) {
            this._previousRowName = rowName;
            this._rowChanged.value = true;
        }
    };

    private onCardKeyDown = (e: React.KeyboardEvent<HTMLElement>, item: IObservableBoardsWorkItem, column: BoardColumnLayout) => {
        const nodeName = (e.target as HTMLElement).nodeName;
        if (item.value.saving || e.defaultPrevented || nodeName === "INPUT" || nodeName === "TEXTAREA") {
            return;
        }

        this._columnChanged.value = false;
        this._doneChanged.value = false;
        this._rowChanged.value = false;

        // e.metaKey -> Command key on Mac.
        if ((e.keyCode === KeyCode.leftArrow || e.keyCode === KeyCode.rightArrow) && !e.shiftKey && (e.ctrlKey || e.metaKey)) {
            // ctrl+left/right => move card to next/previous column
            const direction = e.keyCode === KeyCode.rightArrow ? Direction.Right : Direction.Left;
            this.changeColumnOnKeyDown(direction, item, column);
        } else if (
            isInProgress(column) &&
            (e.keyCode === KeyCode.upArrow || e.keyCode === KeyCode.downArrow) &&
            e.shiftKey &&
            (e.ctrlKey || e.metaKey)
        ) {
            // ctrl+shift+up/down move card between swimlanes
            const direction = e.keyCode === KeyCode.upArrow ? Direction.Up : Direction.Down;
            this.changeLaneOnKeyDown(direction, item, column);
        } else if (
            !isOutgoing(column) &&
            this._hasBacklogManagementPermission &&
            (e.keyCode === KeyCode.upArrow || e.keyCode === KeyCode.downArrow) &&
            !e.shiftKey &&
            (e.ctrlKey || e.metaKey)
        ) {
            // ctrl+up/down reorder card within column
            const direction = e.keyCode === KeyCode.upArrow ? Direction.Up : Direction.Down;
            this.reorderOnKeyDown(direction, item, column, /** reorderToExtreme */ false);
        } else if (
            !isOutgoing(column) &&
            this._hasBacklogManagementPermission &&
            (e.keyCode === KeyCode.home || e.keyCode === KeyCode.end) &&
            !e.shiftKey &&
            (e.ctrlKey || e.metaKey)
        ) {
            // ctrl+home/end move to top/bottom of column
            const direction = e.keyCode === KeyCode.home ? Direction.Up : Direction.Down;
            this.reorderToExtremesOnKeyDown(e, direction, item, column);
        } else if ((e.keyCode === KeyCode.rightArrow || e.keyCode === KeyCode.leftArrow) && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
            // left/right => focus card in previous/next column
            const direction = e.keyCode === KeyCode.rightArrow ? Direction.Right : Direction.Left;
            this.moveFocusAcrossColumns(e, direction, item, column);
        } else if ((e.keyCode === KeyCode.upArrow || e.keyCode === KeyCode.downArrow) && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
            // up/down => focus card above or below
            const direction = e.keyCode === KeyCode.upArrow ? Direction.Up : Direction.Down;
            this.moveFocusWithinColumn(e, direction, item, column);
        } else if (e.keyCode === KeyCode.F10 && e.shiftKey) {
            // handled by <BoardCard>
        }
    };

    private onPageAdditionalItems = async (column: BoardColumnLayout, filter?: string): Promise<void> => {
        const kanbanService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
        await kanbanService.loadNextPartialWorkItems(column, filter);
    };

    private updateNavigationGraph = () => {
        const { boardLayout } = this.props;
        if (!boardLayout.value || !boardLayout.value.columns.length) {
            return;
        }

        const { incomingColumn, outgoingColumn, rows } = boardLayout.value;

        // get incoming, outgoing, and partial in-progress columns
        const visibleColumns = [incomingColumn, ...rows.flatMap(rows => rows.columns), outgoingColumn];
        this._navigationGraph = new ColumnNavigationGraph(visibleColumns, rows.length, boardLayout.value.filteredIds);

        return true;
    };

    private getColumnIdFromRowIdAndItemId(rowId: string | undefined, itemId: number, kanbanService?: IKanbanBoardService): string | undefined {
        if (!rowId) {
            return undefined;
        }

        kanbanService = kanbanService ?? this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");

        const rowColumns = kanbanService.getRowLayout(rowId)?.columns;
        if (!rowColumns || rowColumns.length === 0) {
            return undefined;
        }

        const itemColumnLayout = kanbanService.getWorkItemColumnLayout(itemId);
        return rowColumns.some(column => column.id === itemColumnLayout?.id) ? itemColumnLayout?.id : rowColumns[0].id;
    }

    private changeColumnOnKeyDown(direction: Direction.Left | Direction.Right, item: IObservableBoardsWorkItem, column: BoardColumnLayout) {
        let nextColumn: BoardColumnLayout | undefined;
        if (direction === Direction.Left) {
            nextColumn = this._navigationGraph.getPreviousColumn(column);
        } else {
            nextColumn = this._navigationGraph.getNextColumn(column);
        }

        this.moveToColumn(item.value.id, nextColumn);
    }

    private changeLaneOnKeyDown(direction: Direction.Up | Direction.Down, item: IObservableBoardsWorkItem, column: BoardColumnLayout) {
        let nextColumn: BoardColumnLayout | undefined;
        if (direction === Direction.Up) {
            nextColumn = this._navigationGraph.getAboveColumn(column);
        } else {
            nextColumn = this._navigationGraph.getBelowColumn(column);
        }

        this.moveToColumn(item.value.id, nextColumn);
    }

    private async moveToColumn(itemId: number, column?: BoardColumnLayout, previousId?: number, nextId?: number) {
        if (column) {
            const kanbanService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
            await kanbanService.moveItem(itemId, column.id, column.rowId, !!column.isDone, previousId, nextId);
            focusCard(itemId);
        }
    }

    private reorderToExtremesOnKeyDown(
        event: React.KeyboardEvent<HTMLElement>,
        direction: Direction.Up | Direction.Down,
        item: IObservableBoardsWorkItem,
        column: BoardColumnLayout
    ) {
        this.reorderOnKeyDown(direction, item, column, /** reorderToExtreme */ true);
        event.preventDefault();
        event.stopPropagation();
    }

    private reorderOnKeyDown(
        direction: Direction.Up | Direction.Down,
        item: IObservableBoardsWorkItem,
        column: BoardColumnLayout,
        reorderToExtreme = false
    ) {
        const currentIndex = column.workItems.value.findIndex(workItem => workItem.value.id === item.value.id);
        if (currentIndex < 0) {
            return;
        }

        let moveToIndex = -1;
        const filteredWorkItems = this.getFilteredWorkItemsInColumn(column);
        if (filteredWorkItems.length === column.workItems.value.length) {
            if (direction === Direction.Up) {
                moveToIndex = reorderToExtreme ? 0 : currentIndex - 1;
            } else if (direction === Direction.Down && currentIndex < column.workItems.length - 1) {
                moveToIndex = reorderToExtreme ? column.workItems.length - 1 : currentIndex + 1;
            }
        } else {
            const filteredCurrentIndex = filteredWorkItems.findIndex(workItem => workItem.value.id === item.value.id);
            if (filteredCurrentIndex < 0) {
                return;
            }

            let filteredItem: IObservableBoardsWorkItem | undefined;
            if (direction === Direction.Up && filteredCurrentIndex > 0) {
                filteredItem = filteredWorkItems[filteredCurrentIndex - 1];
            } else if (direction === Direction.Down && filteredCurrentIndex < filteredWorkItems.length - 1) {
                filteredItem = filteredWorkItems[filteredCurrentIndex + 1];
            }

            if (filteredItem) {
                moveToIndex = column.workItems.value.findIndex(workItem => workItem.value.id === filteredItem!.value.id);
            }
        }

        if (moveToIndex < 0) {
            return;
        }

        const itemAtMoveToIndex = column.workItems.value[moveToIndex];
        const [previousId, nextId] = this.getColumnPlacementIndexes(column, itemAtMoveToIndex.value.id, direction === Direction.Down);
        if (previousId || nextId) {
            this.moveToColumn(item.value.id, column, previousId, nextId);
        }
    }

    private updateReorderMarker(
        draggedItemId: number,
        column?: HTMLElement,
        columnRect?: DOMRect,
        closestItem?: HTMLElement,
        closestItemId?: number,
        below?: boolean,
        previousId?: number,
        nextId?: number,
        isDoneChangedOnly?: boolean
    ) {
        if (
            columnRect &&
            column &&
            closestItem &&
            closestItemId &&
            closestItemId !== draggedItemId &&
            (
                (previousId !== draggedItemId &&
                    nextId !== draggedItemId) ||
                isDoneChangedOnly
            )
        ) {
            // calculate the insertion marker location
            const closestRect = closestItem.getBoundingClientRect();
            this._insertionMarker.value = {
                // padding between cards is 8px, insertion marker is 2px
                // so we want to orient it 3px down from the upper bound or 5px up from the lower bound
                top: below ? closestRect.bottom + 3 : closestRect.top - 5,
                left: columnRect.left,
                width: column.offsetWidth
            };
        } else {
            // no marker on the board given the current pointer position, so clear it
            this.clearReorderMarker();
        }
    }

    private clearReorderMarker() {
        this._insertionMarker.value = undefined;
    }

    private getColumnPlacementIndexes(column?: BoardColumnLayout, closestItemId?: number, below?: boolean): [number | undefined, number | undefined] {
        if (!column || !closestItemId || isOutgoing(column)) {
            return [undefined, undefined];
        }
        const closestItemIndex = column.workItems.value.findIndex(item => item.value.id === closestItemId);
        const nextClosestItemIndex = closestItemIndex + (below ? 1 : -1);
        let previousId: number | undefined;
        let nextId: number | undefined;
        if (below) {
            previousId = closestItemId;
            nextId = nextClosestItemIndex < column.workItems.length ? column.workItems.value[nextClosestItemIndex].value.id : undefined;
        } else {
            previousId = nextClosestItemIndex > -1 ? column.workItems.value[nextClosestItemIndex].value.id : undefined;
            nextId = closestItemId;
        }

        return [previousId, nextId];
    }

    private moveFocusAcrossColumns(
        event: React.KeyboardEvent<HTMLElement>,
        direction: Direction,
        item: IObservableBoardsWorkItem,
        column: BoardColumnLayout
    ): void {
        let updatedFocus = false;

        if (direction === Direction.Up) {
            // focus the last card in the above column when moving up
            const nextColumn = this._navigationGraph.getAboveColumn(column, /** isNonEmpty */ true);
            if (nextColumn) {
                const workItems = this.getFilteredWorkItemsInColumn(nextColumn);

                const lastItemId = workItems[workItems.length - 1].value.id;
                updatedFocus = focusCard(lastItemId);
            }
        } else {
            let nextColumn: BoardColumnLayout | undefined;
            // any other direction, focus the first card
            if (direction === Direction.Down) {
                nextColumn = this._navigationGraph.getBelowColumn(column, /** isNonEmpty */ true);
            } else if (direction === Direction.Left) {
                nextColumn = this._navigationGraph.getPreviousColumn(column, /** isNonEmpty */ true);
            } else if (direction === Direction.Right) {
                nextColumn = this._navigationGraph.getNextColumn(column, /** isNonEmpty */ true);
            }

            if (nextColumn) {
                const workItems = this.getFilteredWorkItemsInColumn(nextColumn);
                
                const lastItemId = workItems[0].value.id;
                updatedFocus = focusCard(lastItemId);
            }
        }

        if (updatedFocus) {
            event.preventDefault();
        }
    }

    private moveFocusWithinColumn(
        event: React.KeyboardEvent<HTMLElement>,
        direction: Direction.Down | Direction.Up,
        item: IObservableBoardsWorkItem,
        column: BoardColumnLayout
    ): void {
        let updatedFocus = false;
        const itemId = item.value.id;

        const workItems = this.getFilteredWorkItemsInColumn(column);

        const currentIndex = workItems.findIndex(item => item.value.id === itemId);
        if (currentIndex < 0) {
            return;
        }

        let moveToIndex = -1;
        if (direction === Direction.Up && currentIndex > 0) {
            moveToIndex = currentIndex - 1;
        } else if (direction === Direction.Down && currentIndex < workItems.length - 1) {
            moveToIndex = currentIndex + 1;
        } else {
            // Move outside the scope of the current member (if any)
            this.moveFocusAcrossColumns(event, direction, item, column);
            return;
        }

        if (moveToIndex > -1) {
            const nextItemId = workItems[moveToIndex].value.id;
            updatedFocus = focusCard(nextItemId);
        }

        if (updatedFocus) {
            event.preventDefault();
        }
    }

    private getFilteredWorkItemsInColumn(column: BoardColumnLayout): IObservableBoardsWorkItem[] {
        const filteredIds = this.props.boardLayout.value?.filteredIds;
        if (!filteredIds || filteredIds.size === 0) {
            return column.workItems.value;
        }

        return column.workItems.value.filter(workItem => filteredIds.has(workItem.value.id));
    }
}

/**
 * NOTE: these values must be kept in sync with CSS variables in KanbanBoard.scss
 */
const maxColumnWidth = 350;
const minColumnWidth = 220;
const columnMarginRight = 4;
const totalMaxColumnWidth = maxColumnWidth + columnMarginRight;
const totalMinColumnWidth = minColumnWidth + columnMarginRight;
